/**
 * A finger on the phone's hours — one follower for everything a finger does
 * there besides scrolling and swiping the day: the hold that makes an hour
 * (Aaron, Oct 25, note 5), the hold that lifts something to move it (Oct 27,
 * note 1.1.4 — Outlook's), and edit mode's drags of a block and its handles.
 *
 * Pointer events, never mouse events (`docs/activity.md`, the map's lesson):
 * a finger never sends `mousedown`. Before it is armed a finger that moves
 * further than the slop is a scroll, and the browser has it; once armed, the
 * page stops scrolling under it (a non-passive `touchmove` is the one way to
 * say so after the touch began), and once it moves, near the top or the
 * bottom of the hours they scroll under the finger, faster nearer the edge,
 * with whatever it is carrying going along.
 *
 * The element the finger went down on must stay in the page until it is
 * lifted: a touch's moves are sent to that element, and once it has left the
 * document they no longer reach the window, the browser scrolls and the drag
 * is lost. So whatever follows the finger is drawn beside it, never instead.
 */
import { SLOP_PX, edgeScroll } from './phone-time.js'

export interface FingerEnd {
  /** It held long enough — or needed no hold — and was following. */
  armed: boolean
  /** It went further than the slop from where it went down. */
  moved: boolean
  /** `up`: lifted; `cancel`: the browser took it back; `scroll`: it moved before it was held. */
  how: 'up' | 'cancel' | 'scroll'
}

export interface FingerAt {
  x: number
  y: number
  /** How far it has gone down the hours since it went down — its own move and their scrolling together. */
  dy: number
}

/** The rows pinned at the top of the hours (a person's head): the edge zone starts under them. */
function pinnedHeight(scroller: HTMLElement): number {
  return [...scroller.querySelectorAll<HTMLElement>('[data-grid-pinned]')].reduce((sum, row) => sum + row.offsetHeight, 0)
}

export function followFinger(options: {
  /** The press it starts from. */
  at: { clientX: number; clientY: number; pointerId: number }
  /** The hours' own scroller. */
  scroller: HTMLElement
  /** How long it must hold still first; 0 follows at once (edit mode's handles and block). */
  holdMs: number
  onArm?: () => void
  onFollow: (finger: FingerAt) => void
  onEnd: (end: FingerEnd) => void
}): void {
  const { at, scroller, holdMs, onArm, onFollow, onEnd } = options
  const x0 = at.clientX
  const y0 = at.clientY
  const scroll0 = scroller.scrollTop
  let x = x0
  let y = y0
  let armed = false
  let moved = false
  let done = false
  let frame = 0
  let timer = 0

  const follow = () => onFollow({ x, y, dy: y - y0 + scroller.scrollTop - scroll0 })
  const edge = () => {
    frame = 0
    if (!armed || done) return
    /* Not until it moves: something held near the foot of the screen stays put until it is dragged. */
    if (!moved) {
      frame = window.requestAnimationFrame(edge)
      return
    }
    const box = scroller.getBoundingClientRect()
    const speed = edgeScroll(y, box.top + pinnedHeight(scroller), box.bottom)
    if (speed !== 0) {
      const before = scroller.scrollTop
      scroller.scrollTop = before + speed
      if (scroller.scrollTop !== before) follow()
    }
    frame = window.requestAnimationFrame(edge)
  }
  const arm = () => {
    armed = true
    onArm?.()
    frame = window.requestAnimationFrame(edge)
  }
  const stopScroll = (event: TouchEvent) => { if (armed) event.preventDefault() }
  const finish = (how: FingerEnd['how']) => {
    if (done) return
    done = true
    window.clearTimeout(timer)
    if (frame) window.cancelAnimationFrame(frame)
    window.removeEventListener('pointermove', move)
    window.removeEventListener('pointerup', up)
    window.removeEventListener('pointercancel', up)
    window.removeEventListener('touchmove', stopScroll)
    onEnd({ armed, moved, how })
  }
  function move(event: PointerEvent) {
    if (event.pointerId !== at.pointerId) return
    x = event.clientX
    y = event.clientY
    if (!moved && Math.hypot(x - x0, y - y0) > SLOP_PX) moved = true
    if (!armed) {
      if (moved) finish('scroll')
      return
    }
    follow()
  }
  function up(event: PointerEvent) {
    if (event.pointerId !== at.pointerId) return
    finish(event.type === 'pointerup' ? 'up' : 'cancel')
  }
  window.addEventListener('pointermove', move)
  window.addEventListener('pointerup', up)
  window.addEventListener('pointercancel', up)
  window.addEventListener('touchmove', stopScroll, { passive: false })
  if (holdMs <= 0) arm()
  else timer = window.setTimeout(() => { if (!done) arm() }, holdMs)
}
